import React, {useContext, useState} from "react";
import {
    Avatar,
    Button,
    ButtonBase,
    Stack,
    Toolbar,
    Typography,
    AppBar,
    Menu, Box, MenuItem, ListItemIcon, ListItemText
} from "@mui/material";
import {Link, NavLink, useLocation, useNavigate} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, ConstructionIcon, Key01Icon, Logout01Icon} from "@hugeicons/core-free-icons";
import {HamburgerButton} from "../../../components/HamburgerButton";
import {SignInContext} from "../../../context/SignInContext";
import {HeaderDrawer} from "./HeaderDrawer";
import {WebSettingsContext} from "../../../context/WebSettingsContext";
import {MODEL_LINKS} from "./modelLinks";
import {getRoleColor} from "../../../utils/roleColors";
import {getInitials} from "../../../utils/userDisplay";


const navLinkSx = {
    borderRadius: "999px",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    color: "#6B6B80",
    px: 2,
    py: 0.875,
    minWidth: 0,
    transition: "background-color 0.15s ease, color 0.15s ease",
    "&.active": {
        color: "#4B55C9",
        bgcolor: "rgba(132, 145, 247, 0.14)",
    },
    "&:hover": {
        color: "#23264D",
        bgcolor: "#F4F4FA",
    },
};

const primaryRole = (user) => user?.authorityList?.map(item => item?.authority)?.[0] || user?.roles?.[0] || "";

export const Header = () => {
    const {isAuth, currentUser, onLogout, openModal} = useContext(SignInContext);
    const [openDrawer, setOpenDrawer] = useState(false);

    const isAdmin = currentUser?.roles?.includes("admin")
    const isManager = currentUser?.roles?.includes("manager")


    return (
        <>
            <HeaderDrawer
                open={openDrawer}
                handleClose={() => setOpenDrawer(false)}
                isAuth={isAuth}
                isAdmin={isAdmin}
                isManager={isManager}
                currentUser={currentUser}
                onLogout={onLogout}
                openModal={openModal}
            />

            <AppBar position="static"
                    elevation={0}
                    sx={{
                        bgcolor: "#fff",
                        boxShadow: "0 1px 2px rgba(35, 38, 77, 0.03)",
                        zIndex: 100,
                        borderBottom: "1px solid #ECECF3",
                        minHeight: "48px !important",
                    }}
            >
                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        display: {xs: 'none', md: 'flex'},
                        zIndex: 100,
                        py: 1, px: 2,
                        minHeight: "48px !important",
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={2} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <ButtonBase
                                component={Link}
                                to={"/projects"}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.625,
                                    borderRadius: "999px", "&:hover": {bgcolor: "#F4F4FA"},
                                }}
                            >
                                <img src={"/logo.svg"} alt={"logo"} width={28}/>
                                <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>BlockFlow</Typography>
                            </ButtonBase>
                        </Stack>

                        <Stack direction={"row"} gap={0.5} alignItems={"center"}>
                            {isAdmin && <>
                                <Button
                                    component={NavLink}
                                    to={"/projects"}
                                    end
                                    sx={navLinkSx}
                                >
                                    Projects
                                </Button>
                                <Button
                                    component={NavLink}
                                    to={"/organizations"}
                                    sx={navLinkSx}
                                >
                                    Organizations
                                </Button>
                                <Button
                                    component={NavLink}
                                    to={"/users"}
                                    sx={navLinkSx}
                                >
                                    Users
                                </Button>
                            </>}
                            {(isAdmin || isManager) && <>
                                <ModelsMenu isAdmin={isAdmin}/>
                                <Button
                                    component={NavLink}
                                    to={"/backend-configuration"}
                                    sx={navLinkSx}
                                >
                                    Backend Configuration
                                </Button>
                            </>}

                            {
                                isAuth ?
                                    <Box sx={{ml: 0.5}}>
                                        <UserAvatar currentUser={currentUser}/>
                                    </Box>
                                    :
                                    <Button
                                        onClick={() => openModal()}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: 13,
                                            fontWeight: 700,
                                            px: 2.5,
                                            py: 0.875,
                                            borderRadius: "999px",
                                            color: "#fff",
                                            bgcolor: "#8491F7",
                                            "&:hover": {bgcolor: "#6B78E8"},
                                        }}
                                    >
                                        Sign in
                                    </Button>
                            }

                        </Stack>
                    </Stack>
                </Toolbar>

                <Toolbar
                    sx={{
                        bgcolor: "transparent",
                        py: 0,
                        minHeight: "44px !important",
                        display: {xs: 'flex', md: "none"}
                    }}
                >
                    <Stack direction={"row"} sx={{width: "100%"}} gap={2} justifyContent={"space-between"}
                           alignItems={"center"}>
                        <ButtonBase
                            component={Link}
                            to={"/projects"}
                            sx={{borderRadius: "999px", p: 0.5}}
                        >
                            <img src={"/logo.svg"} alt={"logo"} width={30}/>
                        </ButtonBase>

                        <HamburgerButton onClick={() => setOpenDrawer(true)} sx={{width: 30, height: 30}}/>
                    </Stack>
                </Toolbar>
            </AppBar>
        </>
    )
}

const ModelsMenu = ({isAdmin}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const {pathname} = useLocation();
    const links = isAdmin ? MODEL_LINKS : MODEL_LINKS.filter(link => !link.adminOnly);
    const isActive = links.some(link => pathname.startsWith(link.to));

    return (
        <>
            <Button
                className={isActive ? "active" : undefined}
                sx={navLinkSx}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={16}/>}
            >
                Models
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                PaperProps={{sx: {
                    borderRadius: "14px", minWidth: 220, mt: 0.75, p: 0.5,
                    boxShadow: "0 20px 50px rgba(35, 38, 77, 0.14)",
                }}}
            >
                {links.map(link => (
                    <MenuItem
                        key={link.to}
                        component={NavLink}
                        to={link.to}
                        selected={pathname.startsWith(link.to)}
                        onClick={() => setAnchorEl(null)}
                        sx={{
                            py: 1, px: 1.5, borderRadius: "10px", fontSize: 13.5,
                            "&.Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9"},
                            "&.Mui-selected:hover": {bgcolor: "rgba(132, 145, 247, 0.18)"},
                            "&:hover": {bgcolor: "#F4F4FA"},
                        }}
                    >
                        <ListItemIcon sx={{minWidth: 32, color: "inherit"}}>
                            <HugeiconsIcon icon={link.icon} size={18}/>
                        </ListItemIcon>
                        <ListItemText primary={link.label} primaryTypographyProps={{fontSize: 13.5, fontWeight: 600}}/>
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

const accountMenuItemSx = {
    mx: 1, my: 0.25, px: 1.5, py: 1, borderRadius: "10px", fontSize: 13.5, color: "#4A4A63",
    "&:hover": {bgcolor: "#F4F4FA"},
};

export const UserAvatar = ({currentUser}) => {
    const {onLogout} = useContext(SignInContext);
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const isAdmin = currentUser?.roles?.includes("admin")
    const {settings, onUnderMaintenance} = useContext(WebSettingsContext);
    const navigate = useNavigate();

    const role = primaryRole(currentUser);
    const roleColor = getRoleColor(role);
    const initials = getInitials(currentUser);
    const fullName = [currentUser?.name, currentUser?.lastname].filter(Boolean).join(" ") || currentUser?.username;

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const logoutHandle = async () => {
        try {
            await onLogout();
            handleCloseUserMenu();
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Box sx={{flexGrow: 0}}>
            <ButtonBase
                onClick={handleOpenUserMenu}
                sx={{
                    display: "flex", alignItems: "center", gap: 0.875, pl: 0.5, pr: 1, py: 0.5,
                    borderRadius: "999px", bgcolor: Boolean(anchorElUser) ? "#F4F4FA" : "transparent",
                    "&:hover": {bgcolor: "#F4F4FA"},
                }}
            >
                <Avatar sx={{width: 32, height: 32, fontSize: 13.5, fontWeight: 700, bgcolor: `${roleColor}1F`, color: roleColor}}>
                    {initials}
                </Avatar>
                <Typography sx={{
                    fontSize: 13, fontWeight: 600, color: "#23264D", maxWidth: 120,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    display: {xs: "none", lg: "block"},
                }}>
                    {fullName}
                </Typography>
                <HugeiconsIcon icon={ArrowDown01Icon} size={14} color={"#8A8AA0"}/>
            </ButtonBase>
            <Menu
                sx={{p: 0}}
                id={"menu-appbar"}
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                MenuListProps={{sx: {py: 0.75}}}
                PaperProps={{sx: {
                    borderRadius: "16px", minWidth: "240px", mt: 0.75,
                    boxShadow: "0 20px 50px rgba(35, 38, 77, 0.16)",
                }}}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <Stack direction={"row"} gap={1.25} alignItems={"center"} sx={{px: 2, py: 1.5}}>
                    <Avatar sx={{width: 40, height: 40, fontSize: 16, fontWeight: 700, bgcolor: `${roleColor}1F`, color: roleColor}}>
                        {initials}
                    </Avatar>
                    <Box sx={{minWidth: 0}}>
                        <Typography noWrap sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{fullName}</Typography>
                        {role && (
                            <Box component={"span"} sx={{
                                display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.375, px: 0.875, py: 0.125,
                                borderRadius: "999px", fontSize: 11, fontWeight: 600, textTransform: "capitalize",
                                color: roleColor, bgcolor: `${roleColor}1F`,
                            }}>
                                {role}
                            </Box>
                        )}
                    </Box>
                </Stack>

                <Box sx={{height: 1, bgcolor: "#F2F2F7", mx: 1.5, mb: 0.5}}/>
                <MenuItem onClick={() => {
                    handleCloseUserMenu();
                    navigate("/api-keys");
                }}
                          sx={accountMenuItemSx}>
                    <ListItemIcon sx={{minWidth: 32, color: "#8A8AA0"}}>
                        <HugeiconsIcon icon={Key01Icon} size={18}/>
                    </ListItemIcon>
                    <ListItemText primary={"API Keys"}/>
                </MenuItem>
                {/*{*/}
                {/*    isAdmin &&*/}
                {/*    <MenuItem onClick={onUnderMaintenance}*/}
                {/*              sx={{*/}
                {/*                  ...accountMenuItemSx,*/}
                {/*                  ...(settings?.underMaintenance && {color: "#DC2626"}),*/}
                {/*              }}>*/}
                {/*        <ListItemIcon sx={{minWidth: 32, color: settings?.underMaintenance ? "#DC2626" : "#8A8AA0"}}>*/}
                {/*            <HugeiconsIcon icon={ConstructionIcon} size={18}/>*/}
                {/*        </ListItemIcon>*/}
                {/*        <ListItemText primary={"Under Maintenance"}/>*/}
                {/*    </MenuItem>*/}
                {/*}*/}

                <Box sx={{height: 1, bgcolor: "#F2F2F7", mx: 1.5, my: 0.5}}/>

                <MenuItem onClick={logoutHandle} sx={accountMenuItemSx}>
                    <ListItemIcon sx={{minWidth: 32, color: "#8A8AA0"}}>
                        <HugeiconsIcon icon={Logout01Icon} size={18}/>
                    </ListItemIcon>
                    <ListItemText primary={"Logout"}/>
                </MenuItem>
            </Menu>
        </Box>
    )
}
